import type {
  NodeExecutionDto,
  ProcessEdgeDto,
  ProcessNodeDto,
} from '@api/processes';

/**
 * Per-node test-run gating surfaced in the editor. The Run button on a Task
 * node is enabled when:
 *   • the node itself is `task` + runnable (has skills, instruction, ports), and
 *   • every upstream input port has a source node whose latest test-run
 *     execution status is `completed`. If the source is an Input node it has
 *     no execution, so we treat it as available unconditionally (its artifact
 *     comes from the run-config form, not from a node-execution).
 *
 * The hook returns a per-node descriptor the TaskNode renderer reads to drive
 * the badge + Run button enabled state + tooltip text.
 */
export type NodeRunGateState =
  | { readonly canRun: true }
  | { readonly canRun: false; readonly reason: 'not_runnable' }
  | { readonly canRun: false; readonly reason: 'read_only' }
  | { readonly canRun: false; readonly reason: 'run_in_progress' }
  | {
      readonly canRun: false;
      readonly reason: 'upstream_pending';
      readonly missingUpstreamLabels: readonly string[];
    };

interface NodeView {
  readonly nodeId: string;
  readonly kind: ProcessNodeDto['kind'];
  readonly label: string;
  readonly isRunnable: boolean;
  readonly latestExecution: NodeExecutionDto | null;
  readonly ports: readonly ProcessNodeDto['ports'][number][];
}

/**
 * Compute whether a single node is allowed to run, and if not, why.
 *
 * `nodeId` is the node we want to run. `allNodes` + `allEdges` describe the
 * graph. The function walks one level of upstream connections — an Input
 * node satisfies its dependency immediately (its data arrives via run-config
 * form), every other upstream node must have a `completed` execution.
 *
 * Recursive completeness (e.g. Task A → Task B → Task C, run C) is enforced
 * naturally: if B isn't completed, C is gated until the user runs B; running
 * B in turn gates on A.
 */
export function computeRunGate(
  nodeId: string,
  allNodes: readonly NodeView[],
  allEdges: readonly ProcessEdgeDto[],
): NodeRunGateState {
  const node = allNodes.find((n) => n.nodeId === nodeId);
  if (!node) return { canRun: false, reason: 'not_runnable' };
  // The backend's `isRunnable` flag is the single source of truth for
  // "the node is well-formed enough to execute". Note nodes never carry
  // `isRunnable=true` (they are decorative). Gate nodes don't run from
  // the editor — they wait for a human decision when reached during a
  // full run — so we defer to the same flag without special-casing the
  // type here.
  if (!node.isRunnable) {
    return { canRun: false, reason: 'not_runnable' };
  }

  // Index by nodeId for O(1) upstream lookup.
  const byId = new Map<string, NodeView>(
    allNodes.map((n) => [n.nodeId, n] as const),
  );

  // Collect the set of upstream nodeIds via incoming edges.
  const upstreamIds = new Set<string>();
  for (const edge of allEdges) {
    if (edge.targetNodeId === nodeId) upstreamIds.add(edge.sourceNodeId);
  }

  const missing: string[] = [];
  for (const upId of upstreamIds) {
    const up = byId.get(upId);
    if (!up) continue;
    // Input nodes don't run — their artifact comes from the run config form
    // when the actual run starts. For single-node-run gating they're always
    // satisfied (the user hasn't started a run yet, so the gating only cares
    // about Task / Gate completion).
    if (up.kind === 'input') continue;
    if (up.kind === 'note') continue;
    const status = up.latestExecution?.status ?? 'idle';
    if (status !== 'completed') {
      missing.push(up.label || up.nodeId);
    }
  }

  if (missing.length > 0) {
    return {
      canRun: false,
      reason: 'upstream_pending',
      missingUpstreamLabels: missing,
    };
  }
  return { canRun: true };
}

/**
 * Render the gate's reason as a tooltip for the disabled Run button. Stable
 * formatting so the message is testable.
 */
export function gateTooltip(state: NodeRunGateState): string {
  if (state.canRun) return 'Run this node end-to-end.';
  switch (state.reason) {
    case 'not_runnable':
      return 'Configure this node to enable Run.';
    case 'read_only':
      return 'Published processes cannot be run from the editor — duplicate to a draft first.';
    case 'run_in_progress':
      return 'Another run is already in progress.';
    case 'upstream_pending': {
      const list = state.missingUpstreamLabels.join(', ');
      return `Run upstream nodes first: ${list}`;
    }
  }
}
